import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useSite } from '../hooks/useSites';
import { useToast } from '../hooks/useToast';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { useEditorPreview } from '../hooks/useEditorPreview';
import { getSchema } from '../api/schemas';
import { getContentId } from '../types';
import type { FieldSchema } from '../types';
import FormField from '../components/FormField';
import '../styles/editor.css';

const CTA_FIELDS = ['ctaTitle', 'ctaSubtitle', 'ctaButtonText', 'ctaButtonLink'];

export default function CtaEditor() {
  const { siteId } = useParams<{ siteId: string }>();
  const { site, loading: siteLoading } = useSite(siteId);
  const { items, loading, create, update } = useContent(siteId, 'site');
  const { addToast } = useToast();
  const { sendUpdate } = useEditorPreview('/');

  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [originalData, setOriginalData] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);

  const schema = site ? getSchema(site.templateId) : undefined;
  // Use raw schema fields directly — CTA fields are hidden by default and may be
  // absent from the template manifest, so we skip manifest filtering here.
  const rawFields = schema?.contentTypes.site?.fields || {};
  const existingItem = items[0];

  const isDirty = JSON.stringify(formData) !== JSON.stringify(originalData);
  useUnsavedChanges(isDirty);

  useEffect(() => {
    if (existingItem) {
      const data = existingItem.data || {};
      const subset: Record<string, unknown> = {};
      for (const name of CTA_FIELDS) {
        subset[name] = (data as Record<string, unknown>)[name];
      }
      setFormData(subset);
      setOriginalData(subset);
    }
  }, [existingItem]);

  const handleChange = (name: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    sendUpdate('site', name, value);
  };

  const handleSave = async () => {
    if (!siteId) return;
    setSaving(true);
    try {
      const fullData = { ...(existingItem?.data || {}), ...formData };
      if (existingItem) {
        const contentId = getContentId(existingItem.contentKey);
        await update(contentId, fullData as Record<string, unknown>);
      } else {
        await create(fullData as Record<string, unknown>);
      }
      setOriginalData({ ...formData });
      addToast('success', 'Call to action saved');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading || siteLoading) {
    return (
      <div className="page">
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Call to Action</h1>
        <div className="page-header-actions">
          {isDirty && <span className="unsaved-indicator">Unsaved changes</span>}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <p className="field-hint" style={{ marginBottom: '1rem' }}>
        This section appears near the bottom of your homepage. Use it to encourage visitors to take action — like booking an appointment, visiting your shop, or getting in touch.
      </p>
      <div className="editor-form">
        {CTA_FIELDS.map((name) => {
          const fieldSchema = rawFields[name] as FieldSchema | undefined;
          if (!fieldSchema) return null;
          return (
            <FormField
              key={name}
              name={name}
              schema={{ ...fieldSchema, hidden: false }}
              value={formData[name]}
              onChange={handleChange}
              siteId={siteId!}
              cmsType="cta"
            />
          );
        })}
      </div>
    </div>
  );
}
